import { useCallback, useEffect, useRef, useState } from 'react';
import { useSnackbar } from 'notistack';
import { translateKey } from 'src/locales/translate';
import { dictationSessionApi } from 'src/api/dictation';
import { micLock } from '../utils/micLock';

interface UseDictationOptions {
  onInterim: (text: string) => void;
  onFinal: (text: string) => void;
}

interface UseDictationResult {
  isRecording: boolean;
  isConnecting: boolean;
  start: () => Promise<void>;
  stop: () => void;
}

interface SocketMessage {
  type: 'started' | 'partial' | 'final' | 'stopped' | 'error';
  text?: string;
  message?: string;
}

const RECONNECT_LIMIT = 3;

export function useDictation({
  onInterim,
  onFinal,
}: UseDictationOptions): UseDictationResult {
  const { enqueueSnackbar } = useSnackbar();
  const [isRecording, setIsRecording] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);

  const socketRef = useRef<WebSocket | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const processorRef = useRef<ScriptProcessorNode | null>(null);
  const recordingRef = useRef(false);
  const reconnectAttemptsRef = useRef(0);
  const isConnectingRef = useRef(false);

  const cleanupMedia = useCallback((): void => {
    if (processorRef.current) {
      processorRef.current.disconnect();
      processorRef.current.onaudioprocess = null;
      processorRef.current = null;
    }
    if (audioContextRef.current) {
      void audioContextRef.current.close();
      audioContextRef.current = null;
    }
    streamRef.current?.getTracks().forEach((track) => track.stop());
    streamRef.current = null;
  }, []);

  const teardown = useCallback((): void => {
    recordingRef.current = false;
    isConnectingRef.current = false;
    setIsRecording(false);
    setIsConnecting(false);
    cleanupMedia();
    micLock.release('dictation');
  }, [cleanupMedia]);

  const connectSocket = useCallback(
    (socketUrl: string, token: string): void => {
      const url = new URL(socketUrl, window.location.origin);
      url.searchParams.set('token', token);
      const socket = new WebSocket(url.toString());
      socketRef.current = socket;

      socket.onmessage = (event): void => {
        const message = JSON.parse(event.data) as SocketMessage;
        if (message.type === 'partial') onInterim(message.text || '');
        if (message.type === 'final') onFinal(message.text || '');
        if (message.type === 'error') {
          enqueueSnackbar(
            message.message || translateKey('dictation.connectionLost'),
            { variant: 'error' },
          );
          reconnectAttemptsRef.current = RECONNECT_LIMIT;
          recordingRef.current = false;
          socketRef.current = null;
          socket.close();
          teardown();
        }
        if (message.type === 'stopped') {
          socketRef.current = null;
          teardown();
        }
      };

      socket.onclose = (): void => {
        socketRef.current = null;
        if (
          !recordingRef.current ||
          reconnectAttemptsRef.current >= RECONNECT_LIMIT
        ) {
          if (recordingRef.current) {
            enqueueSnackbar(translateKey('dictation.connectionLost'), {
              variant: 'error',
            });
            teardown();
          }
          return;
        }
        reconnectAttemptsRef.current += 1;
        window.setTimeout(() => connectSocket(socketUrl, token), 750);
      };
    },
    [enqueueSnackbar, onFinal, onInterim, teardown],
  );

  const start = useCallback(async (): Promise<void> => {
    if (recordingRef.current || isConnectingRef.current) return;
    if (!micLock.tryAcquire('dictation')) {
      enqueueSnackbar(translateKey('dictation.micBusy'), {
        variant: 'warning',
      });
      return;
    }

    isConnectingRef.current = true;
    setIsConnecting(true);
    try {
      const { data } = await dictationSessionApi.createSession();
      reconnectAttemptsRef.current = 0;
      recordingRef.current = true;
      connectSocket(data.socketUrl, data.token);

      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          channelCount: 1,
          echoCancellation: true,
          noiseSuppression: true,
        },
      });
      streamRef.current = stream;

      // The Gemini Live API expects raw 16-bit PCM @ 16 kHz mono. MediaRecorder
      // only emits webm/opus (which the Live API rejects), so capture PCM via
      // the Web Audio API and stream it as base64.
      const AudioContextCtor =
        window.AudioContext ||
        (window as unknown as { webkitAudioContext: typeof AudioContext })
          .webkitAudioContext;
      const audioContext = new AudioContextCtor({ sampleRate: 16000 });
      audioContextRef.current = audioContext;
      const sampleRate = audioContext.sampleRate;
      const source = audioContext.createMediaStreamSource(stream);
      const processor = audioContext.createScriptProcessor(4096, 1, 1);
      processorRef.current = processor;
      processor.onaudioprocess = (event): void => {
        if (socketRef.current?.readyState !== WebSocket.OPEN) return;
        const input = event.inputBuffer.getChannelData(0);
        const pcm16 = new Int16Array(input.length);
        for (let i = 0; i < input.length; i += 1) {
          const s = Math.max(-1, Math.min(1, input[i]));
          pcm16[i] = s < 0 ? s * 0x8000 : s * 0x7fff;
        }
        const bytes = new Uint8Array(pcm16.buffer);
        let binary = '';
        for (let i = 0; i < bytes.length; i += 1) {
          binary += String.fromCharCode(bytes[i]);
        }
        socketRef.current.send(
          JSON.stringify({
            type: 'audio',
            data: window.btoa(binary),
            mimeType: `audio/pcm;rate=${sampleRate}`,
          }),
        );
      };
      // Output buffer is left silent (we never write to it), so connecting to
      // the destination doesn't cause feedback — it just keeps the node pulling.
      source.connect(processor);
      processor.connect(audioContext.destination);
      setIsRecording(true);
      isConnectingRef.current = false;
      setIsConnecting(false);
    } catch (error) {
      const name = (error as Error)?.name;
      const key =
        name === 'NotAllowedError' || name === 'NotFoundError'
          ? 'dictation.permissionDenied'
          : 'dictation.startFailed';
      enqueueSnackbar((error as Error)?.message || translateKey(key), {
        variant: 'error',
      });
      socketRef.current?.close();
      socketRef.current = null;
      teardown();
    }
  }, [connectSocket, enqueueSnackbar, teardown]);

  const stop = useCallback((): void => {
    recordingRef.current = false;
    reconnectAttemptsRef.current = RECONNECT_LIMIT; // prevent reconnect
    const socket = socketRef.current;
    if (socket?.readyState === WebSocket.OPEN) {
      // Ask the server to finalize; it will send `stopped` and then close.
      socket.send(JSON.stringify({ type: 'stop' }));
    } else {
      socket?.close();
      socketRef.current = null;
    }
    // Release the mic + lock now rather than waiting on the server round-trip,
    // so a dropped/crashed server can never leak the singleton micLock.
    teardown();
  }, [teardown]);

  useEffect(
    () => () => {
      socketRef.current?.close();
      socketRef.current = null;
      teardown();
    },
    [teardown],
  );

  return { isRecording, isConnecting, start, stop };
}
